import { Txt } from "../common/text";
import { Google } from "./google-maps";
import { Dialog } from "../common/dialog";
import { Application } from "../navigation/application";

export interface LocationInfo
{
    Latitude: number;
    Longitude: number;
    Radius?: number;
    GoogleId: string;
    Address: string;
}

export class GoogleMapsModal
{
    private readonly startPlace: LocationInfo;
    private readonly askRadius: boolean;

    constructor(startPlace: LocationInfo, askRadius: boolean)
    {
        this.startPlace = startPlace;
        this.askRadius = askRadius;
    }

    public showModal(onSave: (result?: LocationInfo) => void)
    {
        const modal = new Dialog(Txt.selectLocation);
        modal.dialog.classList.add("w-dialog-lg");
        modal.onHidden(() => modal.remove());

        const modalBody = modal.body;
        modalBody.style.cssText = "height:520px;position:relative;display:flex;flex-direction:column;gap:8px";

        const autocompleteElement = document.createElement("gmp-place-autocomplete") as any;
        autocompleteElement.id = "autocomplete";
        autocompleteElement.style.borderRadius = "4px";

        const autocompleteContainer = document.createElement("div");
        autocompleteContainer.appendChild(autocompleteElement);
        modalBody.appendChild(autocompleteContainer);

        const mapContainer = document.createElement("div");
        mapContainer.style.cssText = "flex:1;border:1px solid #ccc";
        modalBody.appendChild(mapContainer);

        let radiusInput: HTMLInputElement | null = null;

        if (this.askRadius)
        {
            const radiusContainer = document.createElement("div");
            radiusContainer.style.cssText =
                "display:flex;gap:8px;align-items:center;margin-top:4px";

            const radiusLabel = document.createElement("label");
            radiusLabel.innerText = Txt.radius;
            radiusLabel.style.fontWeight = "600";

            radiusInput = document.createElement("input");
            radiusInput.type = "number";
            radiusInput.className = "form-control";
            radiusInput.style.width = "120px";
            radiusInput.min = "100";
            radiusInput.max = "10000";
            radiusInput.step = "100";
            radiusInput.value = String(this.startPlace?.Radius ?? Application.defaultRadius);

            radiusContainer.append(radiusLabel, radiusInput);
            modalBody.appendChild(radiusContainer);
        }

        const modalFooter = document.createElement("div");
        modalFooter.className = "w-dialog-footer";

        const saveBtn = document.createElement("button");
        saveBtn.className = "btn btn-primary";
        saveBtn.textContent = Txt.save;

        const clearBtn = document.createElement("button");
        clearBtn.className = "btn btn-secondary";
        clearBtn.textContent = Txt.clear;

        const closeBtn = document.createElement("button");
        closeBtn.className = "btn btn-default";
        closeBtn.textContent = Txt.close;

        modalFooter.append(saveBtn, clearBtn, closeBtn);
        modalBody.after(modalFooter);

        const start = { lat: this.startPlace?.Latitude ?? 58.686057, lng: this.startPlace?.Longitude ?? 25.842476 };
        const map = new Google.maps.Map(mapContainer, { zoom: 7, center: start });
        const marker = new Google.maps.Marker({ position: start, map, draggable: true });

        if (radiusInput != null)
        {
            const circle = new Google.maps.Circle({
                map,
                radius: parseInt(radiusInput.value, 10),
                fillColor: "rgba(128, 0, 128, 0.3)",
                strokeColor: "purple",
                strokeOpacity: 0.7
            });
            circle.bindTo("center", marker, "position");

            radiusInput.addEventListener("change", () =>
            {
                let val = parseInt(radiusInput.value, 10) || 500;
                val = Math.max(100, Math.min(10000, val));
                radiusInput.value = val.toString();
                circle.setRadius(val);
            });
        }

        let tempPlace = this.startPlace;

        autocompleteElement.addEventListener("gmp-select", async (event: any) =>
        {
            const prediction = event.placePrediction;
            const place = prediction.toPlace();
            await place.fetchFields({ fields: ["formattedAddress", "location"] });

            const loc = place.location;

            tempPlace = {
                Latitude: loc.lat(),
                Longitude: loc.lng(),
                GoogleId: place.id ?? "",
                Address: place.formattedAddress ?? ""
            };

            marker.setPosition(loc);
            map.setCenter(loc);
            map.setZoom(14);
        });

        map.addListener("click", e =>
        {
            const lat = e.latLng.lat();
            const lng = e.latLng.lng();

            marker.setPosition(e.latLng);

            tempPlace = {
                Latitude: lat,
                Longitude: lng,
                GoogleId: "",
                Address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`
            };
        });

        saveBtn.onclick = () =>
        {
            if (radiusInput)
            {
                const radius = Math.max(
                    100,
                    Math.min(
                        10000,
                        parseInt(radiusInput.value, 10) || Application.defaultRadius
                    )
                );
                onSave(tempPlace ? { ...tempPlace, Radius: radius } : null);
            }
            else
            {
                onSave(tempPlace ? { ...tempPlace } : null);
            }

            modal.hide();
        };

        clearBtn.onclick = () =>
        {
            tempPlace = null;
            onSave(null);
            modal.hide();
        };

        closeBtn.onclick = () =>
        {
            tempPlace = null;
            modal.hide();
        };

        modal.onShown(() => Google.maps.event.trigger(map, "resize", {}));
        modal.show();
    }
}
